/* eslint-disable unicorn/no-null */
import EmberObject from '@ember/object';
import Service, { inject as service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { preciseRound } from '@latermedia/ember-later-analytics/utils';

import { filterTimeSeries } from 'later/utils/array-filters';

import type TableService from 'later/services/analytics/formatters/table';
import type HelpersAnalyticsService from 'later/services/analytics/helpers-analytics';
import type { Moment } from 'moment';
import type { FormattedDynamoLinkedinPost, DefaultLinkedinPostRow } from 'shared/types/analytics-data/media';

export default class LinkedinPostsService extends Service {
  @service('analytics/helpers-analytics') declare helpersAnalytics: HelpersAnalyticsService;
  @service('analytics/formatters/table') declare tableFormatters: TableService;

  /**
   * Key names to be used to form table columns
   */
  names = [
    'impressions',
    'membersReached',
    'reactions',
    'commentsPost',
    'reshares',
    'views',
    'viewers',
    'timeWatched',
    'clickCount',
    'commentCount',
    'impressionCount',
    'likeCount',
    'shareCount',
    'uniqueImpressionsCount',
    'videoViewCount',
    'viewerCount',
    'organizationTimeWatched'
  ];

  get defaultRow(): DefaultLinkedinPostRow {
    return {
      id: null,
      url: null,
      createdAt: '',
      createdAtTime: '',
      timestampLongFormat: '',
      createdAtUnix: null,
      imageUrl: null,
      isVideo: false,
      isCarousel: false,
      impressions: this.tableFormatters.buildDefaultTableObject(),
      membersReached: this.tableFormatters.buildDefaultTableObject(),
      reactions: this.tableFormatters.buildDefaultTableObject(),
      commentsPost: this.tableFormatters.buildDefaultTableObject(),
      reshares: this.tableFormatters.buildDefaultTableObject(),
      views: this.tableFormatters.buildDefaultTableObject(),
      viewers: this.tableFormatters.buildDefaultTableObject(),
      timeWatched: this.tableFormatters.buildDefaultTableObject(),
      clickCount: this.tableFormatters.buildDefaultTableObject(),
      commentCount: this.tableFormatters.buildDefaultTableObject(),
      engagement: this.tableFormatters.buildDefaultTableObject(),
      impressionCount: this.tableFormatters.buildDefaultTableObject(),
      likeCount: this.tableFormatters.buildDefaultTableObject(),
      shareCount: this.tableFormatters.buildDefaultTableObject(),
      uniqueImpressionsCount: this.tableFormatters.buildDefaultTableObject(),
      videoViewCount: this.tableFormatters.buildDefaultTableObject(),
      viewerCount: this.tableFormatters.buildDefaultTableObject(),
      organizationTimeWatched: this.tableFormatters.buildDefaultTableObject()
    };
  }

  /**
   * Takes an array of posts, formats each post for the
   * posts table according to the given post key names
   *
   * @returns Array of posts formatted for the posts table
   */
  processPosts(
    posts: FormattedDynamoLinkedinPost[] = [],
    startDate: Moment,
    endDate: Moment,
    analyticsStandard: boolean
  ): FormattedDynamoLinkedinPost[] {
    if (isEmpty(posts) || !Array.isArray(posts)) {
      return posts;
    }

    const filteredPosts = analyticsStandard
      ? filterTimeSeries(posts, startDate.unix(), endDate.unix(), 'createdAtUnix')
      : posts;

    return filteredPosts
      .map((post) => {
        const values = this.tableFormatters.getValues(this.names, post, filteredPosts);
        const tableRow = this.tableFormatters.buildTableRow(values);
        const imageUrl = post?.imageUrl ?? 'assets/images/img--analytics--text-post.png';

        // Convert the tableRow array to a single object
        const tableRowObject = Object.assign({}, ...tableRow);

        const engagementPercent = this.tableFormatters.isValid(post.engagement ?? '')
          ? Number(post.engagement) * 100
          : undefined;

        const processedPost = Object.assign(
          {},
          {
            id: post.id,
            url: imageUrl,
            thUrl: post?.imageUrl,
            type: post.mediaType || null,
            isVideo: post.mediaType === 'video',
            isCarousel: post.mediaType === 'carousel',
            createdAt: this.helpersAnalytics.createMomentInTz(post.createdAtUnix).format('MMM D, YYYY') || '-',
            createdAtTime: this.helpersAnalytics.createMomentInTz(post.createdAtUnix).format('LT') || '-',
            timestampLongFormat:
              this.helpersAnalytics.createMomentInTz(post.createdAtUnix).format('MMM D, YYYY hh:mm:ss A') || null,
            caption: post.text || null,
            createdAtUnix: post.createdAtUnix,
            // Rendered in a plain text cell (not a heatmap), matching how every
            // other platform presents its engagement *rate* — so no `scaled`
            // 1–100 heatmap value is needed here.
            engagement: {
              value: engagementPercent !== undefined ? `${engagementPercent.toFixed(2)}%` : '-',
              CSVValue: engagementPercent !== undefined ? `${preciseRound(engagementPercent, 2)}%` : null,
              raw: engagementPercent !== undefined ? Number(post.engagement) : null
            }
          },
          tableRowObject
        );

        return EmberObject.create(processedPost);
      })
      .reverse();
  }
}

declare module '@ember/service' {
  interface Registry {
    'analytics/formatters/table/linkedin-posts': LinkedinPostsService;
  }
}
